import { useEffect, useState } from "react";
import { ChevronDown, Package } from "lucide-react";
import { EstimateNotice } from "../ui/estimate-notice";
import { DimensionInput } from "../ui/dimension-input";
import { useFormikContext } from "formik";
import type { ShipProcessingFee } from "@/schemas/ship";
import type { UnknownFormValues } from "../types/shipment-calculator.type";

export function CalculateForUnknownSea({
  inputClassName,
  processingFees,
}: {
  inputClassName: string;
  processingFees: ShipProcessingFee[];
}) {
  const { handleChange, setFieldValue, values } = useFormikContext<UnknownFormValues>();
  const [error, setError] = useState<string | null>(null);
  let estimatedWeight = +values.length * +values.width * +values.height * (+values.quantity || 1);
  estimatedWeight = values.measurementUnit === "cm" ? estimatedWeight / 1000000 : estimatedWeight;
  useEffect(() => {
    void setFieldValue("estimated_weight", String(estimatedWeight), false);
  }, [setFieldValue, estimatedWeight]);

  useEffect(() => {
    const minRange = processingFees?.[0]?.start_range;
    const maxRange = processingFees?.[processingFees.length - 1]?.end_range;

    // No fee data loaded yet (or it failed to load) — nothing to validate against.
    if (minRange === undefined || maxRange === undefined) {
      setError(null);
      return;
    }

    if (
      values.length &&
      values.width &&
      values.height &&
      (estimatedWeight < minRange || estimatedWeight > maxRange)
    ) {
      setError(
        `Volume must be at least ${minRange} cbm and at most ${maxRange} cbm. Adjust dimensions accordingly.`,
      );
    } else {
      setError(null);
    }
  }, [estimatedWeight, processingFees, values.length, values.width, values.height]);

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-xl font-medium text-[#323232]">Calculate from dimension</h3>

      <label className="block">
        <span className="mb-4 block text-base font-normal text-[#323232]">
          Select Measurement unit (default is Meters)
        </span>
        <span className="relative block">
          <select
            name="measurementUnit"
            value={values.measurementUnit}
            onChange={handleChange}
            className={`${inputClassName} appearance-none pr-12 text-primary-text-200`}
          >
            <option value="">Select type</option>
            <option value="cm">Centimeters</option>
            <option value="m">Meters</option>
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-6 top-1/2 h-5 w-5 -translate-y-1/2 text-black"
            aria-hidden="true"
          />
        </span>
      </label>

      <div className="grid gap-6 sm:grid-cols-2">
        <DimensionInput
          label="Enter Length"
          name="length"
          inputClassName={inputClassName}
          placeholder="Length"
        />
        <DimensionInput
          label="Enter Width"
          name="width"
          inputClassName={inputClassName}
          placeholder="Width"
        />
        <DimensionInput
          label="Enter Height"
          name="height"
          inputClassName={inputClassName}
          placeholder="Height"
        />
        <DimensionInput
          label="Enter Quantity"
          name="quantity"
          inputClassName={inputClassName}
          placeholder="Quantity (default is 1)"
        />
      </div>

      <div className="flex items-center gap-2 rounded-sm border border-[#cbd6e2] bg-[#eef4fd] px-2 py-4 text-sm text-black">
        <Package className="h-5 w-5 shrink-0 text-[#323232]" aria-hidden="true" />
        <p>Estimated CBM is {estimatedWeight.toFixed(2)} cbm</p>
      </div>

      {error && <p className="mt-2 p-2 text-sm text-red-500">{error}</p>}
      <EstimateNotice />
    </div>
  );
}

